import { Component, input, output, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

export interface TestLogEntry {
  message: string;
  severity: 'info' | 'success' | 'error';
}

@Component({
  selector: 'tf-settings-test-sms-dialog',
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './test-sms-dialog.html',
  styleUrl: './test-sms-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestSmsDialog {
  readonly visible = input<boolean>(false);
  readonly visibleChange = output<boolean>();
  readonly send = output<string>();
  readonly logEntries = input<TestLogEntry[]>([]);
  readonly testInProgress = input<boolean>(false);

  readonly phoneNumber = signal<string>('');
  readonly isTouched = signal<boolean>(false);

  readonly isValid = computed(() => {
    const phone = this.phoneNumber().trim();

    // Phone must start with + and be followed by at least 7 digits (can have spaces/dashes)
    // E.164 format: +[country code][subscriber number]
    // Example: +1234567890, +49 123 456789, +1-234-567-8900
    const phoneRegex = /^\+[0-9\s\-()]{7,}$/;

    if (!phoneRegex.test(phone)) {
      return false;
    }

    // Extract only digits after the +
    const digits = phone.substring(1).replace(/[\s\-()]/g, '');

    // Must have at least 7 digits and contain only numbers
    return digits.length >= 7 && /^\d+$/.test(digits);
  });

  readonly showError = computed(() => {
    return this.isTouched() && !this.isValid() && this.phoneNumber().trim().length > 0;
  });

  readonly isSendDisabled = computed(() => {
    return !this.isValid() || this.testInProgress();
  });

  onVisibleChange(visible: boolean): void {
    this.visibleChange.emit(visible);
    if (!visible) {
      this.phoneNumber.set('');
      this.isTouched.set(false);
    }
  }

  onClose(): void {
    this.onVisibleChange(false);
  }

  onSend(): void {
    this.isTouched.set(true);
    if (this.isValid() && !this.testInProgress()) {
      this.send.emit(this.phoneNumber());
    }
  }

  onPhoneNumberChange(value: string): void {
    this.phoneNumber.set(value);
    if (!this.isTouched()) {
      this.isTouched.set(true);
    }
  }
}
